import { madridDateRange } from '../utils/madridDateRange';
import React, { useEffect, useState } from 'react';
import { apiFetch, parseJsonSafe } from '../utils/api';

const AIUsagePage = () => {
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState({
    totalCost: 0,
    totalRequests: 0,
    models: [],
    modules: []
  });
  const [sortDescending, setSortDescending] = useState(true);
  const [filters, setFilters] = useState({ start: '', end: '' });

  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    window.location.href = '/login';
  };

  useEffect(() => {
    const fetchUsage = async () => {
      setLoading(true);
      setError('');
      try {
        const qs = new URLSearchParams();
        const range = madridDateRange(filters.start, filters.end);
        if (range.start) qs.append('start', range.start);
        if (range.end) qs.append('end', range.end);
        const res = await apiFetch(`/api/ai/usage?${qs.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await parseJsonSafe(res);
        if (res.status === 401) return handleUnauthorized();
        if (res.ok && data) {
          setSummary({
            totalCost: data.totalCost || 0,
            totalRequests: data.totalRequests || 0,
            models: data.models || [],
            modules: data.modules || []
          });
        } else {
          setError(data?.message || 'No se pudieron cargar logs IA');
        }
      } catch (err) {
        setError('No se pudieron cargar logs IA');
      } finally {
        setLoading(false);
      }
    };
    fetchUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const setRange = (range) => {
    const today = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Madrid' }).format(new Date());
    const end = new Date(`${today}T12:00:00Z`);
    const start = new Date(end);
    if (range === 'week') start.setUTCDate(start.getUTCDate() - 6);
    if (range === 'month') start.setUTCDate(1);
    const format = (date) => date.toISOString().slice(0, 10);
    setFilters({ start: format(start), end: format(end) });
  };
  const byCost = (rows) => [...rows].sort((a, b) => (Number(a.cost || 0) - Number(b.cost || 0)) * (sortDescending ? -1 : 1));

  return (
    <div className="container py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">Logs IA</h4>
          <div className="text-muted small">Control de consumo por modelo y módulo.</div>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setRange('today')}>Hoy</button>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setRange('week')}>7 días</button>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setRange('month')}>Este mes</button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setSortDescending((p) => !p)}>Coste {sortDescending ? '↓' : '↑'}</button>
          <input
            type="date"
            className="form-control form-control-sm"
            value={filters.start}
            onChange={(e) => setFilters((p) => ({ ...p, start: e.target.value }))}
          />
          <input
            type="date"
            className="form-control form-control-sm"
            value={filters.end}
            onChange={(e) => setFilters((p) => ({ ...p, end: e.target.value }))}
          />
          <button className="btn btn-outline-secondary btn-sm" onClick={() => setFilters({ start: '', end: '' })}>
            Limpiar
          </button>
        </div>
      </div>
      {loading && <div className="alert alert-info">Cargando...</div>}
      {error && <div className="alert alert-warning">{error}</div>}

      <div className="row g-3">
        <div className="col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="text-muted small">Coste estimado</div>
              <h4 className="mb-0">€ {summary.totalCost.toFixed(2)}</h4>
              <div className="text-muted small">Total peticiones: {summary.totalRequests}</div>
            </div>
          </div>
        </div>
        <div className="col-md-8">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h6>Consumo por modelo</h6>
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Modelo</th>
                      <th className="text-end">Peticiones</th>
                      <th className="text-end">Coste €</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(summary.models || []).length ? (
                      byCost(summary.models).map((m) => (
                        <tr key={m.name}>
                          <td>{m.name}</td>
                          <td className="text-end">{m.requests}</td>
                          <td className="text-end">€ {Number(m.cost || 0).toFixed(2)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="3" className="text-muted small">Sin datos de modelos.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm mt-3">
        <div className="card-body">
          <h6>Consumo por módulo</h6>
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Módulo</th>
                  <th className="text-end">Peticiones</th>
                  <th className="text-end">Coste €</th>
                </tr>
              </thead>
              <tbody>
                {(summary.modules || []).length ? (
                  byCost(summary.modules).map((m) => (
                    <tr key={m.name}>
                      <td>{m.name}</td>
                      <td className="text-end">{m.requests}</td>
                      <td className="text-end">€ {Number(m.cost || 0).toFixed(2)}</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="3" className="text-muted small">Sin datos de módulos.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIUsagePage;
